import { useState } from 'react';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import { Switch } from '../../../../components/ui/switch';
import { updateUser } from '../../../../api/user';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toastifyInstance from '../../../../utils/toastify';
import SelectComponent from '../../../../components/select';
import { RoleMemberProject, UserRole } from '../../../../constants/user';

interface InlineEditProps {
    data: any;
    field: string;
    onCancel: () => void;
}

const InlineEdit = ({ data, field, onCancel }: InlineEditProps) => {
    const [value, setValue] = useState(data[field]);
    const queryClient = useQueryClient();

    const updateUserMutation = useMutation({
        mutationFn: async (newValue: any) => {
            const updateData = {
                [field]: newValue,
                _id: data._id
            };
            return updateUser(updateData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            toastifyInstance.success('User updated successfully');
            onCancel();
        },
        onError: () => {
            toastifyInstance.error('Failed to update user');
        }
    });

    const handleSubmit = () => {
        let finalValue = value;
        if (typeof value === 'boolean') {
            finalValue = Boolean(value);
        }
        updateUserMutation.mutate(finalValue);
    };

    const renderInput = () => {
        switch (field) {
            case 'isWorking':
            case 'allowedManualLog':
            case 'allowedOT':
            case 'allowedWorkAtWeekend':
            case 'allowedWorkAtNoonTime':
            case 'allowedWebTracking':
            case 'deactivated':
            case 'skipScreenshot':
            case 'skipCheckAutoClick':
            case 'skipActivityCheck':
            case 'skipIdenticalImagesCheck':
            case 'skipHeartbeatCheck':
            case 'allowedWorkOnHoliday':
                return (
                    <Switch
                        checked={Boolean(value)}
                        onCheckedChange={(checked) => setValue(Boolean(checked))}
                    />
                );
            case 'role':
                const roleOptions = Object.values(UserRole).map(role => ({
                    label: role.charAt(0).toUpperCase() + role.slice(1),
                    value: role
                }));
                return (
                    <SelectComponent
                        options={roleOptions}
                        defaultValue={value}
                        onChange={(newValue) => setValue(newValue)}
                        placeholder="Select role"
                    />
                );
            case 'position':
                const positionOptions = Object.values(RoleMemberProject).map(role => ({
                    label: role,
                    value: role
                }));
                return (
                    <SelectComponent
                        options={positionOptions}
                        defaultValue={value}
                        onChange={(newValue) => setValue(newValue)}
                        placeholder="Select position"
                    />
                );
            case 'avatarUrl':
                return (
                    <div className="flex items-center gap-2">
                        <img src={value} alt="avatar" className="w-8 h-8 rounded-full object-cover" />
                        <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                        setValue(reader.result);
                                    };
                                    reader.readAsDataURL(file);
                                }
                            }}
                            className="hidden"
                            id="avatar-upload"
                        />
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => document.getElementById('avatar-upload')?.click()}
                        >
                            Change Avatar
                        </Button>
                    </div>
                );
            case 'birthDate':
            case 'joinDate':
                return (
                    <Input
                        type="date"
                        value={value || ''}
                        onChange={(e) => setValue(e.target.value)}
                    />
                );
            case 'dailyTrackingTimeLimit':
                return (
                    <Input
                        className="w-320"
                        type="number"
                        value={value}
                        defaultValue={value || 450}
                        onChange={(e) => setValue(e.target.value)}
                    />
                );
            default:
                return (
                    <Input
                        value={value || ''}
                        onChange={(e) => setValue(e.target.value)}
                    />
                );
        }
    };

    return (
        <div className="flex items-center gap-2">
            {renderInput()}
            <Button size="sm" onClick={handleSubmit}>Save</Button>
            <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        </div>
    );
};

export default InlineEdit; 